import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
} from "@mui/material";
import { CARD_SURFACE_SX, DIVIDER_COLOR, MUTED_TEXT_COLOR } from "../../../styles/tokens";

const NUMERIC_SX = { fontVariantNumeric: "tabular-nums", textAlign: "right", whiteSpace: "nowrap" };

// Фиксированная раскладка: таблица не шире карточки, название забирает остаток и переносится
const COLUMN_WIDTHS = {
  rank: 36,
  orders: { xs: 72, md: 88 },
  value: { xs: 104, md: 140 },
};

const CELL_PX = { xs: 1, md: 1.5 };

const headCellSx = {
  color: MUTED_TEXT_COLOR,
  fontSize: "13px",
  fontWeight: 500,
  backgroundColor: "transparent",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
  py: 1,
  px: CELL_PX,
  whiteSpace: "nowrap",
};

const bodyCellSx = (isLast) => ({
  color: "#fff",
  fontSize: "0.9rem",
  borderBottom: isLast ? "none" : `1px solid ${DIVIDER_COLOR}`,
  py: 1.25,
  px: CELL_PX,
  verticalAlign: "middle",
});

/**
 * Карточка с таблицей топ-5 блюд
 */
const TopDishesTable = ({ title, subtitle = null, data, loading, valueKey, valueFormat, barColor = "#66bb6a", emptyText = "Нет данных" }) => {
  const formatValue = (value) => {
    if (valueFormat) {
      return valueFormat(value);
    }
    return typeof value === "number"
      ? value.toLocaleString("ru-RU", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : value;
  };

  const maxValue = Math.max(0, ...(data || []).map((dish) => Number(dish[valueKey]) || 0));

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, px: 2, pb: 2 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              width="100%"
              height={44}
              sx={{ borderRadius: 1 }}
            />
          ))}
        </Box>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Typography sx={{ color: "rgba(255,255,255,0.6)", textAlign: "center", py: 3, px: 2 }}>
          {emptyText}
        </Typography>
      );
    }

    return (
      <Table sx={{ tableLayout: "fixed", width: "100%" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headCellSx, width: COLUMN_WIDTHS.rank, pl: 2 }} />
            <TableCell sx={headCellSx}>Блюдо</TableCell>
            <TableCell sx={{ ...headCellSx, ...NUMERIC_SX, width: COLUMN_WIDTHS.orders }}>Заказов</TableCell>
            <TableCell sx={{ ...headCellSx, ...NUMERIC_SX, width: COLUMN_WIDTHS.value, pr: 2 }}>Показатель</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((dish, index) => {
            const isLast = index === data.length - 1;
            const value = Number(dish[valueKey]) || 0;
            const barPercent = maxValue > 0 ? Math.max(0, Math.min(100, (value / maxValue) * 100)) : 0;

            return (
              <TableRow
                key={`${dish.dishName}-${index}`}
                sx={{ "&:hover": { backgroundColor: "rgba(255,255,255,0.03)" } }}
              >
                {/* Номер места */}
                <TableCell
                  sx={{
                    ...bodyCellSx(isLast),
                    pl: 2,
                    color: MUTED_TEXT_COLOR,
                    fontSize: "13px",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  <Box component="span" sx={{ display: "inline-block", width: 20 }}>
                    {index + 1}
                  </Box>
                </TableCell>

                {/* Название: на десктопе 1 строка, на телефоне до 2; полное — в title */}
                <TableCell sx={bodyCellSx(isLast)}>
                  <Box
                    component="span"
                    title={dish.dishName}
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: { xs: 2, md: 1 },
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      wordBreak: "break-word",
                      lineHeight: 1.35,
                    }}
                  >
                    {dish.dishName}
                  </Box>
                </TableCell>

                <TableCell sx={{ ...bodyCellSx(isLast), ...NUMERIC_SX, color: "rgba(255,255,255,0.9)" }}>
                  {/* Заказ на несколько групп iiko делит дробями — в таблице целые */}
                  {Math.round(dish.ordersCount).toLocaleString("ru-RU")}
                </TableCell>

                {/* Значение и полоска относительно максимума в таблице */}
                <TableCell sx={{ ...bodyCellSx(isLast), pr: 2 }}>
                  <Typography sx={{ ...NUMERIC_SX, fontSize: "0.9rem", fontWeight: 600, color: "#fff" }}>
                    {formatValue(dish[valueKey])}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      height: 4,
                      borderRadius: "2px",
                      backgroundColor: "rgba(255,255,255,0.06)",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${barPercent}%`,
                        height: "100%",
                        borderRadius: "2px",
                        backgroundColor: barColor,
                      }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box
      sx={{
        ...CARD_SURFACE_SX,
        width: "100%",
        maxWidth: { xs: 600, md: "100%" },
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Typography sx={{ px: 2, pt: 2, pb: subtitle ? 0.25 : 1.5, color: "#fff", fontSize: "16px", fontWeight: 700 }}>
        {title}
      </Typography>
      {/* Условие отбора — чтобы не искали в топе блюдо, которое под него не подходит */}
      {subtitle && (
        <Typography sx={{ px: 2, pb: 1.5, color: MUTED_TEXT_COLOR, fontSize: "13px" }}>{subtitle}</Typography>
      )}
      {renderBody()}
    </Box>
  );
};

export default TopDishesTable;
